import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { signalRService } from '@/shared/services/signalr.service';
import { sileo } from 'sileo';
import type {
  BuzonNucPageResponse,
  BuzonNotificationPageResponse,
  BuzonNotificationIndicators,
  BuzonNoLeidasResponse,
  BuzonInstanciasDisponiblesResponse,
  BitacoraAccionTipo,
} from '@/shared/types/interfaces/BuzonJudicial.interface';
import {
  BUZON_JUDICIAL_UPDATED_EVENT,
  activateNucById,
  activateNucsByIds,
  cancelSolicitud,
  canAccessBuzonJudicialModule as checkAccess,
  deactivateNucById,
  deactivateNucsByIds,
  getAllNucIds,
  getInstanciasDisponibles,
  getNotificationsPage,
  getNucPreferencesPage,
  reactivateNucsByIds,
  registrarVisualizacion,
  registrarAccion,
  getUnreadNotifications,
} from '@/shared/services/BuzonJudicial.service';
import type {
  BuzonDeactivatePayload,
  BuzonReactivatePayload,
  BuzonNotificationPageParams,
  BuzonNucPageParams,
} from '@/shared/types/interfaces/BuzonJudicial.interface';
import { useUserData } from '@/shared/hooks/useUserData.hook';
import type { PerfilEntidad } from '../types/interfaces/user.interface';
import { storage } from '../utils/Const.utils';
import { StorageKeys } from '../types/enums/Storage.enum';

/** Clave de caché compartida para la paginación de NUCs */
export const NUCS_PAGE_QUERY_KEY = 'buzonJudicial-nucsPage';
/** Clave de caché compartida para la paginación de notificaciones */
export const NOTIFICATIONS_PAGE_QUERY_KEY = 'buzonJudicial-notificationsPage';
/** Clave de caché para el conteo de notificaciones no leídas */
export const UNREAD_COUNT_QUERY_KEY = 'buzonJudicial-unreadCount';
/** Clave de caché para las instancias disponibles de un conjunto de casos (uso transitorio, dentro de un modal) */
export const INSTANCIAS_DISPONIBLES_QUERY_KEY = 'buzonJudicial-instanciasDisponibles';

/**
 * Hook para obtener una página de NUCs del buzón judicial.
 */
export const useNucsPage = (params: BuzonNucPageParams) => {
  const isEnabled = Boolean(params.idUsuario && params.idUsuario > 0);

  return useQuery<BuzonNucPageResponse>({
    queryKey: [NUCS_PAGE_QUERY_KEY, params],
    queryFn: () => getNucPreferencesPage(params),
    enabled: isEnabled,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 30, // 30 segundos
  });
};

/**
 * Hook para obtener una página de notificaciones del buzón judicial.
 */
export const useNotificationsPage = (params: BuzonNotificationPageParams) => {
  const isEnabled = Boolean(params.idUsuario && params.idUsuario > 0);

  return useQuery<BuzonNotificationPageResponse>({
    queryKey: [NOTIFICATIONS_PAGE_QUERY_KEY, params],
    queryFn: () => getNotificationsPage(params),
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 30, // 30 segundos
  });
};

/**
 * Hook para obtener las categorías de tribunal (instancias) disponibles para un conjunto de casos.
 * `enabled` se controla desde afuera (solo se dispara cuando el usuario elige el modo "Instancia").
 */
export const useInstanciasDisponibles = (idsCasos: number[], enabled: boolean) => {
  return useQuery<BuzonInstanciasDisponiblesResponse>({
    queryKey: [INSTANCIAS_DISPONIBLES_QUERY_KEY, idsCasos],
    queryFn: () => getInstanciasDisponibles(idsCasos),
    enabled: enabled && idsCasos.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
};

/**
 * Hook centralizado para la lógica de negocio del Buzón Judicial.
 * Deriva el estado de las consultas a la API.
 */
export const useBuzonJudicial = () => {
  const { userData } = useUserData();
  const idUsuario = userData?.id || 0;
  const perfil: PerfilEntidad | null = storage.get(StorageKeys.PERFIL);
  const idRelUsuarioEntidades = perfil?.infoEntidad?.relacion?.idRelUsuarioEntidades || null;
  const queryClient = useQueryClient();

  // Consulta base para obtener indicadores y conteos globales
  const { data: nucsSummary, isLoading: isSummaryLoading, refetch: refresh } = useNucsPage({
    idUsuario,
    pageNumber: 1,
    pageSize: 10,
    idRelUsuarioEntidades
  });

  const { data: notificationsSummary } = useNotificationsPage({
    idUsuario, // Temporalmente fijo para obtener indicadores generales
    pageNumber: 1,
    pageSize: 10,
    idRelUsuarioEntidades
  });

  /** Total de NUCs activos según el helper de la API */
  const activeNucsCount = nucsSummary?.helper?.totalNUCActivos || 0;

  /** Indica si hay NUCs activos */
  const hasActiveNucs = activeNucsCount > 0;

  /** Indicadores de notificaciones según el helper de la API */
  const indicadores = useMemo((): BuzonNotificationIndicators => {
    return notificationsSummary?.helper?.indicadores || {
      totalNotificaciones: 0,
      notificacionCitacionDocumento: 0,
      notificacionDocumento: 0,
      notificacionCitacion: 0,
    };
  }, [notificationsSummary]);

  const invalidateBuzonQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [NUCS_PAGE_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_PAGE_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
    window.dispatchEvent(new Event(BUZON_JUDICIAL_UPDATED_EVENT));
  }, [queryClient]);

  /** Activa un NUC específico */
  const handleActivateNuc = useCallback(async (idCaso: number) => {
    const result = await activateNucById(idCaso, idUsuario, idRelUsuarioEntidades);
    invalidateBuzonQueries();
    return result;
  }, [idUsuario, invalidateBuzonQueries, idRelUsuarioEntidades]);

  /** Activa múltiples NUCs seleccionados */
  const handleActivateSelected = useCallback(async (idsCasos: number[]) => {
    const result = await activateNucsByIds(idsCasos, idUsuario, idRelUsuarioEntidades);
    invalidateBuzonQueries();
    return result;
  }, [idUsuario, invalidateBuzonQueries, idRelUsuarioEntidades]);

  /** Activa uno o más NUCs filtrados por una única categoría de tribunal (habilitación por instancia) */
  const handleActivateConInstancia = useCallback(async (idsCasos: number[], idsCategoriaTribunal: number[]) => {
    return activateNucsByIds(idsCasos, idUsuario, idRelUsuarioEntidades, { tipoHabilitacion: 2, idsCategoriaTribunal });
  }, [idUsuario, idRelUsuarioEntidades]);

  /** Desactiva un NUC específico */
  const handleDeactivateNuc = useCallback(
    async (idCaso: number, payload: BuzonDeactivatePayload) => {
      await deactivateNucById(idCaso, idUsuario, payload, idRelUsuarioEntidades);
      invalidateBuzonQueries();
    },
    [idUsuario, invalidateBuzonQueries, idRelUsuarioEntidades],
  );

  /** Desactiva múltiples NUCs seleccionados */
  const handleDeactivateSelected = useCallback(
    async (idsCasoBuzon: number[], payload: BuzonDeactivatePayload) => {
      await deactivateNucsByIds(idsCasoBuzon, idUsuario, payload, idRelUsuarioEntidades);
      invalidateBuzonQueries();
    },
    [idUsuario, invalidateBuzonQueries, idRelUsuarioEntidades],
  );

  /** Solicita re-habilitación de NUCs deshabilitados */
  const handleReactivateNucs = useCallback(
    async (idsCasoBuzon: number[], payload: BuzonReactivatePayload) => {
      const result = await reactivateNucsByIds(idsCasoBuzon, idUsuario, payload, idRelUsuarioEntidades);
      await queryClient.invalidateQueries({ queryKey: [NUCS_PAGE_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_PAGE_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
      window.dispatchEvent(new Event(BUZON_JUDICIAL_UPDATED_EVENT));
      return result;
    },
    [idUsuario, queryClient, idRelUsuarioEntidades],
  );

  /** Cancela solicitudes pendientes (firma o deshabilitación) */
  const handleCancelSolicitud = useCallback(
    async (idsCasoBuzon: number[]) => {
      await cancelSolicitud(idsCasoBuzon, idUsuario, idRelUsuarioEntidades);
      invalidateBuzonQueries();
    },
    [idUsuario, invalidateBuzonQueries, idRelUsuarioEntidades],
  );

  return {
    state: {//FIXME: Considerar eliminar esta estructura ya que por el momento solo se obtiene el indicador de autoActivarTodoNuc.
      nucs: nucsSummary?.data || [],
      notifications: notificationsSummary?.data || [],
      autoActivarTodoNuc: nucsSummary?.helper?.autoActivarTodosNuc || false,
    },
    isLoading: isSummaryLoading,
    refresh,
    activeNucsCount,
    hasActiveNucs,
    canAccessModule: checkAccess(activeNucsCount),
    indicadores,
    getNotifications: getNotificationsPage,
    getNucs: getNucPreferencesPage,
    getAllNucIds: (parametro?: string) => getAllNucIds(idUsuario, parametro),
    activateNuc: handleActivateNuc,
    activateSelected: handleActivateSelected,
    activateConInstancia: handleActivateConInstancia,
    /** Expuesto para que flujos que disparan varias llamadas en paralelo (ej. habilitar por instancia
     *  agrupado) invaliden las queries UNA sola vez al final, en vez de una vez por llamada. */
    invalidateBuzonQueries,
    deactivateNuc: handleDeactivateNuc,
    deactivateSelected: handleDeactivateSelected,
    reactivateNucs: handleReactivateNucs,
    cancelSolicitud: handleCancelSolicitud,
    registrarVisualizacion: async (idTramite: number, idSolicitudNotificacion: number) => {
      // Optimista: marcar como vista en todas las páginas cacheadas para ocultar el badge al instante
      queryClient.setQueriesData<BuzonNotificationPageResponse>(
        { queryKey: [NOTIFICATIONS_PAGE_QUERY_KEY] },
        (page) => page?.data
          ? {
            ...page,
            data: page.data.map((notificacion) =>
              notificacion.idTramite === idTramite && notificacion.idSolicitudNotificacion === idSolicitudNotificacion
                ? { ...notificacion, visto: true }
                : notificacion,
            ),
          }
          : page,
      );
      try {
        await registrarVisualizacion(idUsuario, idTramite, idSolicitudNotificacion);
      } catch (error) {
        // Si no se registró en la bitácora, volver al estado real del servidor
        queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_PAGE_QUERY_KEY] });
        throw error;
      }
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
    },
    registrarAccion: async (
      idTramite: number,
      idSolicitudNotificacion: number,
      tipoAccion: BitacoraAccionTipo,
      detalle?: string,
    ) => {
      await registrarAccion({ idUsuario, idTramite, idSolicitudNotificacion, tipoAccion, detalle }).catch(() => {});
    },
  };
};

/**
 * Hook que escucha eventos SignalR de cambio de estado del buzón
 * e invalida las queries de React Query para refrescar la UI automáticamente.
 */
export const useBuzonRealtimeUpdates = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleBuzonUpdate = (update: any) => {
      console.log('[Buzón] Actualización en tiempo real:', update);

      queryClient.invalidateQueries({ queryKey: [NUCS_PAGE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_PAGE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
      window.dispatchEvent(new Event(BUZON_JUDICIAL_UPDATED_EVENT));

      // sileo.info(update.mensaje || `Estado del buzón actualizado: ${update.nuevoEstado}`);
    };

    signalRService.on('buzonStatusChanged', handleBuzonUpdate);
    return () => {
      signalRService.off('buzonStatusChanged', handleBuzonUpdate);
    };
  }, [queryClient]);
};

/**
 * Duración total (ms) de la ventana del aviso de Buzón Judicial post-login.
 * Se usa para calcular `expiresAt` al mostrarlo (PostLoginRolForm) y para
 * resumir el tiempo restante si la página se recarga mientras sigue vigente
 * (useBuzonJudicialNotice). Debe coincidir con el `duration` pasado al toast de sileo.
 */
export const BUZON_JUDICIAL_NOTICE_DURATION_MS = 150000;

const selectUnreadCount = (response: BuzonNoLeidasResponse) => response.count;

/**
 * Consulta compartida de GET /no-leidas (una sola petición por usuario/entidad): cada hook
 * consumidor toma de la misma caché solo la parte que necesita mediante `select`.
 */
const useUnreadBuzonQuery = <TData,>(select?: (response: BuzonNoLeidasResponse) => TData) => {
  const { userData } = useUserData();
  const idUsuario = userData?.id || 0;
  const perfil: PerfilEntidad | null = storage.get(StorageKeys.PERFIL);
  const idRelUsuarioEntidades = perfil?.infoEntidad?.relacion?.idRelUsuarioEntidades || null;

  return useQuery<BuzonNoLeidasResponse, Error, TData>({
    queryKey: [UNREAD_COUNT_QUERY_KEY, idUsuario, idRelUsuarioEntidades],
    queryFn: () => getUnreadNotifications(idUsuario, idRelUsuarioEntidades),
    select,
    enabled: idUsuario > 0,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
  });
};

/**
 * Hook para obtener el conteo de notificaciones judiciales no leídas.
 * Se usa en el Sidebar para mostrar un badge.
 */
export const useUnreadBuzonCount = () => useUnreadBuzonQuery(selectUnreadCount);

/**
 * Hook para obtener las notificaciones no leídas con su desglose por tipo (`indicadores`).
 * Se usa en el aviso post-login de Buzón Judicial.
 */
export const useUnreadBuzonNotifications = () => useUnreadBuzonQuery<BuzonNoLeidasResponse>();
